import { Component, OnInit, ViewChild, OnDestroy, Inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Employee, EmployeeDataService, CreateEmployeeInput, UpdateEmployeeInput } from '../../services/employee-data.service';
import { PayrollDataService, Payroll } from '../../services/payroll-data.service';
import { SmallDialogComponent } from 'src/app/dashboard/atoms/small-dialog/small-dialog.component';
import { SalaryCalculationDialogComponent } from 'src/app/dashboard/preview/dialogs/salary-calculation-dialog/salary-calculation-dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-employees-table',
  templateUrl: './employees-table.component.html',
  styleUrls: ['./employees-table.component.css']
})
export class EmployeesTableComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = [
    'select',
    'firstName',
    'lastName',
    'birthDate',
    'jmbg',
    'address',
    'municipalityCode',
    'startDate',
    'endDate',
    'taxCard',
    'grossSalary',
    'actions'
  ];
  
  dataSource = new MatTableDataSource<Employee>([]);
  selection: Set<string> = new Set();
  isLoading = false;
  searchQuery = '';
  isHovered = false;
  isHovered1 = false;
  savedPayrolls: Payroll[] = [];
  
  private subscriptions: Subscription[] = [];
  
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private employeeService: EmployeeDataService,
    private payrollService: PayrollDataService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadEmployees();
    this.loadSavedPayrolls();
    
    this.subscriptions.push(
      this.employeeService.employees$.subscribe(employees => {
        this.dataSource.data = employees;
      }),
      this.employeeService.loading$.subscribe(loading => {
        this.isLoading = loading;
      })
    );
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  async loadEmployees(): Promise<void> {
    const clientId = localStorage.getItem('userID');
    if (clientId) {
      try {
        await this.employeeService.listEmployees(clientId);
      } catch (error) {
        this.snackBar.open('Failed to load employees', 'Dismiss', {
          duration: 3000,
          panelClass: ['white-snackbar']
        });
      }
    }
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  toggleSelection(employee: Employee): void {
    if (this.selection.has(employee.id)) {
      this.selection.delete(employee.id);
    } else {
      this.selection.add(employee.id);
    }
  }

  isSelected(employee: Employee): boolean {
    return this.selection.has(employee.id);
  }

  toggleAllSelection(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.dataSource.data.forEach(emp => this.selection.add(emp.id));
    }
  }

  isAllSelected(): boolean {
    return this.selection.size === this.dataSource.data.length && this.dataSource.data.length > 0;
  }

  openAddEmployeeDialog(): void {
    const dialogRef = this.dialog.open(AddEmployeeDialogComponent, {
      width: '650px',
      data: { isEdit: false }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.snackBar.open('Employee added successfully', 'Dismiss', {
          duration: 3000,
          panelClass: ['white-snackbar']
        });
      }
    });
  }

  openEditEmployeeDialog(employee: Employee): void {
    const dialogRef = this.dialog.open(AddEmployeeDialogComponent, {
      width: '650px',
      data: { isEdit: true, employee }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.snackBar.open('Employee updated successfully', 'Dismiss', {
          duration: 3000,
          panelClass: ['white-snackbar']
        });
      }
    });
  }

  openSalaryCalculation(payrollId?: string): void {
    const dialogRef = this.dialog.open(SalaryCalculationDialogComponent, {
      width: '1100px',
      maxHeight: '90vh',
      data: { payrollId }
    });

    dialogRef.afterClosed().subscribe(() => {
      this.loadSavedPayrolls();
    });
  }

  async loadSavedPayrolls(): Promise<void> {
    const clientId = localStorage.getItem('userID');
    if (clientId) {
      try {
        const result = await this.payrollService.listPayrolls(clientId);
        this.savedPayrolls = result.items;
      } catch (error) {
        console.error('Error loading saved payrolls:', error);
      }
    }
  }

  getPayrollLabel(payroll: Payroll): string {
    const months = ['Januar', 'Februar', 'Mart', 'April', 'Maj', 'Juni', 'Juli', 'August', 'Septembar', 'Oktobar', 'Novembar', 'Decembar'];
    const monthName = months[payroll.month - 1] || '';
    return `${monthName} ${payroll.year} (${payroll.status})`;
  }

  async deleteEmployee(employee: Employee): Promise<void> {
    const dialogData = {
      heading: 'Delete Employee',
      contentPartOne: `Are you sure you want to delete ${employee.fullName}?`,
      action: 'Delete'
    };

    const dialogRef = this.dialog.open(SmallDialogComponent, {
      width: '480px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(async response => {
      if (response?.action === 'do') {
        try {
          await this.employeeService.deleteEmployee(employee.id);
          this.snackBar.open('Employee deleted successfully', 'Dismiss', {
            duration: 3000,
            panelClass: ['white-snackbar']
          });
        } catch (error) {
          this.snackBar.open('Failed to delete employee', 'Dismiss', {
            duration: 3000,
            panelClass: ['white-snackbar']
          });
        }
      }
    });
  }

  async bulkDeleteEmployees(): Promise<void> {
    if (this.selection.size === 0) return;

    const dialogData = {
      heading: 'Delete Selected Employees',
      contentPartOne: `Are you sure you want to delete ${this.selection.size} employee(s)?`,
      action: 'Delete'
    };

    const dialogRef = this.dialog.open(SmallDialogComponent, {
      width: '480px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(async response => {
      if (response?.action === 'do') {
        try {
          const deletePromises = Array.from(this.selection).map(id => 
            this.employeeService.deleteEmployee(id)
          );
          await Promise.all(deletePromises);
          this.selection.clear();
          this.snackBar.open('Employees deleted successfully', 'Dismiss', {
            duration: 3000,
            panelClass: ['white-snackbar']
          });
        } catch (error) {
          this.snackBar.open('Failed to delete some employees', 'Dismiss', {
            duration: 3000,
            panelClass: ['white-snackbar']
          });
        }
      }
    });
  }

  formatCurrency(value: number | undefined): string {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('bs-BA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value) + ' KM';
  }

  formatDate(dateStr: string | undefined): string {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('bs-BA');
    } catch {
      return dateStr;
    }
  }
}

@Component({
  selector: 'app-add-employee-dialog',
  templateUrl: './add-employee-dialog.component.html',
  styleUrls: ['./add-employee-dialog.component.css']
})
export class AddEmployeeDialogComponent implements OnInit {
  employeeForm!: FormGroup;
  isEdit = false;

  constructor(
    public dialogRef: MatDialogRef<AddEmployeeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { isEdit: boolean; employee?: Employee },
    private fb: FormBuilder,
    private employeeService: EmployeeDataService,
    private snackBar: MatSnackBar
  ) {
    this.isEdit = data.isEdit;
  }

  ngOnInit(): void {
    this.employeeForm = this.fb.group({
      firstName: [this.data.employee?.firstName || '', Validators.required],
      lastName: [this.data.employee?.lastName || '', Validators.required],
      birthDate: [this.data.employee?.birthDate || ''],
      birthYear: [this.data.employee?.birthYear || null],
      jmbg: [this.data.employee?.jmbg || ''],
      address: [this.data.employee?.address || ''],
      residence: [this.data.employee?.residence || ''],
      municipalityCode: [this.data.employee?.municipalityCode || ''],
      startDate: [this.data.employee?.startDate || ''],
      endDate: [this.data.employee?.endDate || ''],
      taxCard: [this.data.employee?.taxCard || ''],
      grossSalary: [this.data.employee?.grossSalary || null, [Validators.min(0)]]
    });
  }

  async onSubmit(): Promise<void> {
    if (this.employeeForm.invalid) return;

    const rawValue = this.employeeForm.value;
    const clientID = localStorage.getItem('userID');
    if (!clientID) return;

    // Strip empty strings and null values to avoid NonNull schema errors
    const formValue: any = {};
    for (const [key, val] of Object.entries(rawValue)) {
      if (val !== '' && val !== null && val !== undefined) {
        formValue[key] = val;
      }
    }

    try {
      if (this.isEdit && this.data.employee) {
        const input: UpdateEmployeeInput = {
          id: this.data.employee.id,
          ...formValue
        };
        await this.employeeService.updateEmployee(input);
      } else {
        const input: CreateEmployeeInput = {
          ...formValue,
          idNumber: formValue.jmbg || formValue.firstName + ' ' + formValue.lastName,
          clientID
        };
        await this.employeeService.createEmployee(input);
      }
      this.dialogRef.close(true);
    } catch (error) {
      this.snackBar.open(
        this.isEdit ? 'Failed to update employee' : 'Failed to add employee',
        'Dismiss',
        { duration: 3000, panelClass: ['white-snackbar'] }
      );
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
